export type SpecialModeContext = {
  state?: Record<string, any>;
  beatlineLang?: string;
  t?: (key: string, fallback?: string) => string;
};

function qs<T extends HTMLElement = HTMLElement>(selector: string): T | null {
  return document.querySelector(selector) as T | null;
}

function intValue(value: unknown, fallback: number): number {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) ? n : fallback;
}

function getState(ctx?: SpecialModeContext): Record<string, any> {
  if (ctx?.state) {
    try { (window as any).__beatlineTsLastState = ctx.state; } catch (_) {}
    return ctx.state as Record<string, any>;
  }
  return ((window as any).beatlineState || (window as any).state || (window as any).__beatlineTsLastState || {}) as Record<string, any>;
}

function setDisplay(el: HTMLElement | null, show: boolean, display = 'block'): void {
  if (!el) return;
  el.style.display = show ? display : 'none';
  el.toggleAttribute('hidden', !show);
  el.setAttribute('aria-hidden', show ? 'false' : 'true');
}

function survivalLivesElements(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>('#survival-lives, .survival-lives'));
}

function selectedSpeedrunTarget(state: Record<string, any>): number {
  const selected = document.querySelector<HTMLInputElement>('input[name="speedrun_cards"]:checked')?.value;
  return Math.max(1, intValue(state.speedrunTarget ?? selected ?? 10, 10));
}

function speedrunDone(state: Record<string, any>, target: number): number {
  const p1 = state.p1 || {};
  const score = intValue(p1.score, 0);
  const timelineCards = Array.isArray(p1.timeline) ? Math.max(0, p1.timeline.length - 1) : 0;
  return Math.max(0, Math.min(target, Math.max(score, timelineCards)));
}

function applyScorePanelMode(mode: 'hidden' | 'speedrun' | 'normal', ctx?: SpecialModeContext): void {
  const panel = document.getElementById('active-score-panel');
  if (!panel) return;

  if (mode === 'hidden') {
    panel.style.setProperty('display', 'none', 'important');
    panel.setAttribute('hidden', 'hidden');
    panel.setAttribute('aria-hidden', 'true');
    return;
  }

  panel.removeAttribute('hidden');
  panel.setAttribute('aria-hidden', 'false');
  panel.style.setProperty('display', 'grid', 'important');
  panel.style.setProperty('visibility', 'visible', 'important');

  if (mode !== 'speedrun') return;

  const state = getState(ctx);
  const target = selectedSpeedrunTarget(state);
  const done = speedrunDone(state, target);
  const needed = Math.max(0, target - done);
  const lang = String(ctx?.beatlineLang || (window as any).beatlineLang || 'nl').toLowerCase();
  const countEl = document.getElementById('active-score-count');
  const neededEl = document.getElementById('active-score-needed');

  if (countEl) countEl.textContent = `${done}/${target}`;
  panel.classList.toggle('is-complete', needed <= 0);
  if (neededEl) {
    neededEl.innerHTML = needed <= 0
      ? (lang === 'en'
        ? '<span class="active-score-panel__needed-word">DONE</span>'
        : '<span class="active-score-panel__needed-word">KLAAR</span>')
      : (lang === 'en'
        ? `<span class="active-score-panel__needed-word">NEED</span><strong class="active-score-panel__needed-number">${needed}</strong><span class="active-score-panel__needed-word">MORE</span>`
        : `<span class="active-score-panel__needed-word">NOG</span><strong class="active-score-panel__needed-number">${needed}</strong><span class="active-score-panel__needed-word">NODIG</span>`);
  }
}

function formatStopwatchMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const ss = String(totalSeconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

function getTimeAttackRemainingMsFromState(state: Record<string, any>): number {
  if (!state.timeAttack) return 0;
  return Math.max(0, intValue(state.timeAttackEndAt, Date.now()) - Date.now());
}

function getSpeedrunElapsedMsFromState(state: Record<string, any>): number {
  if (!state.speedrun) return 0;
  if (state.speedrunEnded && intValue(state.speedrunFinalElapsedMs, 0) > 0) return intValue(state.speedrunFinalElapsedMs, 0);
  return Math.max(0, Date.now() - intValue(state.speedrunStartAt, Date.now()));
}

export function setTimeAttackUIFromTypeScript(enabled: boolean, ctx?: SpecialModeContext): { handled: true } {
  const app = document.getElementById('beatline-app');
  if (app) app.classList.toggle('time-attack', !!enabled);

  const timerEl = document.getElementById('timeattack-timer');
  if (timerEl) {
    setDisplay(timerEl, !!enabled, 'block');
    if (!enabled) timerEl.innerText = '00:00';
  }

  if (enabled) applyScorePanelMode('hidden', ctx);
  return { handled: true };
}

export function updateTimeAttackTimerFromTypeScript(ctx?: SpecialModeContext): { handled: boolean; expired?: boolean } {
  const state = getState(ctx);
  if (!state.timeAttack || state.timeAttackEnded) return { handled: true };

  const remaining = getTimeAttackRemainingMsFromState(state);
  const timerEl = document.getElementById('timeattack-timer');
  if (timerEl) {
    const totalSeconds = Math.ceil(remaining / 1000);
    const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const ss = String(totalSeconds % 60).padStart(2, '0');
    timerEl.innerText = `${mm}:${ss}`;
  }
  applyScorePanelMode('hidden', ctx);
  return { handled: true, expired: remaining <= 0 };
}

export function setSpeedrunUIFromTypeScript(enabled: boolean, ctx?: SpecialModeContext): { handled: true } {
  const app = document.getElementById('beatline-app');
  if (app) app.classList.toggle('speedrun', !!enabled);

  const timerEl = document.getElementById('timeattack-timer');
  if (timerEl) {
    setDisplay(timerEl, !!enabled, 'block');
    if (!enabled) timerEl.innerText = '00:00';
  }

  survivalLivesElements().forEach((el) => {
    el.style.display = enabled ? 'none' : '';
    if (!enabled) el.innerText = '❤️❤️❤️';
  });

  if (enabled) applyScorePanelMode('speedrun', ctx);
  return { handled: true };
}

export function updateSpeedrunTimerFromTypeScript(ctx?: SpecialModeContext): { handled: true } {
  const state = getState(ctx);
  if (!state.speedrun || state.speedrunEnded) return { handled: true };

  const elapsed = getSpeedrunElapsedMsFromState(state);
  const timerEl = document.getElementById('timeattack-timer');
  if (timerEl) {
    const base = formatStopwatchMs(elapsed);
    const penalty = intValue(state.speedrunPenaltySeconds, 0);
    timerEl.innerText = penalty > 0 ? `${base} (+${penalty}s)` : base;
  }
  applyScorePanelMode('speedrun', ctx);
  return { handled: true };
}

export function setSurvivalUIFromTypeScript(enabled: boolean, ctx?: SpecialModeContext): { handled: true } {
  const app = document.getElementById('beatline-app');
  if (app) app.classList.toggle('survival', !!enabled);

  survivalLivesElements().forEach((el) => {
    setDisplay(el, !!enabled, 'block');
    if (!enabled) el.innerText = '❤️❤️❤️';
  });

  if (enabled) {
    updateSurvivalLivesUIFromTypeScript(ctx);
    applyScorePanelMode('hidden', ctx);
  }
  return { handled: true };
}

export function updateSurvivalLivesUIFromTypeScript(ctx?: SpecialModeContext): { handled: true } {
  const state = getState(ctx);
  const lives = Math.max(0, intValue(state.survivalLives, 0));
  survivalLivesElements().forEach((el) => {
    el.innerText = '❤️'.repeat(lives);
  });
  applyScorePanelMode('hidden', ctx);
  return { handled: true };
}

export function setPerfectOrderUIFromTypeScript(enabled: boolean, ctx?: SpecialModeContext): { handled: true } {
  const app = document.getElementById('beatline-app');
  if (app) {
    app.classList.toggle('perfect-order', !!enabled);
    if (!enabled) app.classList.remove('perfect-ended');
  }
  if (enabled) applyScorePanelMode('hidden', ctx);
  return { handled: true };
}

export function applyPerfectOrderCardColorsFromTypeScript(rootEl: Element | null): { handled: true } {
  if (!rootEl) return { handled: true };
  const classes = ['po-c1', 'po-c2', 'po-c3', 'po-c4', 'po-c5', 'po-c6'];
  const cards = Array.from(rootEl.querySelectorAll<HTMLElement>('.card'));
  cards.forEach((el, index) => {
    classes.forEach((className) => el.classList.remove(className));
    el.classList.add(classes[index % classes.length]);
  });
  return { handled: true };
}

export type SpecialModeContinueContext = SpecialModeContext & {
  beatlineSetBonusOpen?: (open: boolean) => void;
  nextTurn?: () => void;
  drawCard?: () => void | Promise<void>;
  dealPerfectLevel?: () => void;
  endPerfectOrder?: (reason?: string) => void;
  endTimeAttack?: () => void;
  endSurvival?: () => void;
  endSpeedrun?: (reason?: string) => void;
  getTimeAttackRemainingMs?: () => number;
  activeSongs?: Record<string, any[]>;
  activeSongsBase?: Record<string, any[]>;
};

function closeBonusModalForContinue(ctx?: SpecialModeContinueContext): void {
  const modal = document.getElementById('bonus-modal') as HTMLElement | null;
  if (modal) modal.style.display = 'none';
  try {
    if (typeof ctx?.beatlineSetBonusOpen === 'function') ctx.beatlineSetBonusOpen(false);
    else {
      const app = document.getElementById('beatline-app');
      if (app) app.classList.remove('bonus-open');
    }
  } catch (_) {}
}

function resetPerfectOrderBoardContinueButton(): void {
  const btn = document.getElementById('btn-next') as HTMLButtonElement | null;
  if (!btn || btn.dataset.beatlinePerfectContinue !== '1') return;
  btn.dataset.beatlinePerfectContinue = '0';
  btn.style.display = 'none';
  btn.disabled = false;
  const lang = String((window as any).beatlineLang || 'nl').toLowerCase();
  btn.textContent = lang === 'en' ? 'NEW CARD' : 'NIEUWE KAART';
  btn.onclick = () => {
    const draw = (window as any).drawCard;
    if (typeof draw === 'function') draw();
  };
}




function beatlineShowSurvivalNewCardButtonFromTypeScript(ctx: any): void {
  // beatline_survival_ts_continue_gate_v4
  const state = ctx && ctx.state ? ctx.state : null;

  if (state) {
    state.survivalAwaitingNewCard = true;
    state.survivalNewCardReadyAt = Date.now() + 250;
  }

  const btnNext = document.getElementById('btn-next') as HTMLButtonElement | null;
  const btnCheck = document.getElementById('btn-check') as HTMLButtonElement | null;

  if (btnCheck) {
    btnCheck.style.display = 'none';
    btnCheck.disabled = false;
    btnCheck.style.pointerEvents = 'auto';
  }

  if (btnNext) {
    let label = 'NIEUWE KAART';

    try {
      if (ctx && typeof ctx.t === 'function') {
        label = String(ctx.t('btn_new_card') || label);
      }
    } catch (_) {}

    btnNext.textContent = label;
    btnNext.style.display = 'flex';
    btnNext.disabled = false;
    btnNext.style.pointerEvents = 'auto';
  }
}

export function continueSpecialModeFromTypeScript(ctx?: SpecialModeContinueContext): { handled: boolean; reason?: string } {
  const state = getState(ctx);
  if (!state || !(state.perfectOrder || state.timeAttack || state.survival || state.speedrun)) {
    return { handled: false, reason: 'not-special-mode' };
  }

  closeBonusModalForContinue(ctx);

  if (state.perfectOrder) {
    if (state.perfectEnded) return { handled: true, reason: 'perfect-ended' };
    if (!state.perfectAwaitingNext) return { handled: true, reason: 'perfect-not-awaiting-next' };
    state.perfectAwaitingNext = false;
    resetPerfectOrderBoardContinueButton();
    if (typeof ctx?.dealPerfectLevel === 'function') ctx.dealPerfectLevel();
    else return { handled: false, reason: 'missing-deal-perfect-level' };
    return { handled: true, reason: 'perfect-next-level' };
  }

  if (state.timeAttack) {
    if (state.timeAttackEnded) return { handled: true, reason: 'timeattack-ended' };
    if (typeof ctx?.nextTurn === 'function') ctx.nextTurn(); else return { handled: false, reason: 'missing-next-turn' };
    const remaining = typeof ctx?.getTimeAttackRemainingMs === 'function' ? ctx.getTimeAttackRemainingMs() : 1;
    if (remaining <= 0) { if (typeof ctx?.endTimeAttack === 'function') ctx.endTimeAttack(); return { handled: true, reason: 'timeattack-expired' }; }
    if (!Array.isArray(state.deck) || state.deck.length === 0) { if (typeof ctx?.endTimeAttack === 'function') ctx.endTimeAttack(); return { handled: true, reason: 'timeattack-out' }; }
    if (typeof ctx?.drawCard === 'function') ctx.drawCard(); else return { handled: false, reason: 'missing-draw-card' };
    return { handled: true, reason: 'timeattack-next-card' };
  }

  if (state.survival) {
    const anyCtx = ctx as any;

    if (state.survivalEnded) return { handled: true };

    if ((state.survivalLives || 0) <= 0) {
      if (anyCtx && typeof anyCtx.endSurvival === 'function') anyCtx.endSurvival();
      return { handled: true };
    }

    if (anyCtx && typeof anyCtx.nextTurn === 'function') anyCtx.nextTurn();

    if (Array.isArray(state.deck) && state.deck.length === 0) {
      const pool =
        (anyCtx && anyCtx.activeSongsBase && state.mode && anyCtx.activeSongsBase[state.mode]) ||
        (anyCtx && anyCtx.activeSongs && state.mode && anyCtx.activeSongs[state.mode]) ||
        [];

      if (Array.isArray(pool) && pool.length) {
        state.deck = pool.slice().sort(() => Math.random() - 0.5);
      }
    }

    if (!Array.isArray(state.deck) || state.deck.length === 0) {
      if (anyCtx && typeof anyCtx.endSurvival === 'function') anyCtx.endSurvival();
      return { handled: true };
    }

    beatlineShowSurvivalNewCardButtonFromTypeScript(anyCtx);
    return { handled: true };
  }

  if (state.speedrun) {
    if (state.speedrunEnded) return { handled: true, reason: 'speedrun-ended' };
    if (typeof ctx?.nextTurn === 'function') ctx.nextTurn(); else return { handled: false, reason: 'missing-next-turn' };
    if (!Array.isArray(state.deck) || state.deck.length === 0) { if (typeof ctx?.endSpeedrun === 'function') ctx.endSpeedrun('out'); return { handled: true, reason: 'speedrun-out' }; }
    if (typeof ctx?.drawCard === 'function') ctx.drawCard(); else return { handled: false, reason: 'missing-draw-card' };
    return { handled: true, reason: 'speedrun-next-card' };
  }

  return { handled: false, reason: 'unhandled' };
}
